/**
 * Объекты на стенах вне электрики: сантехника (умывальник, полотенцесушитель, точки воды)
 * и настенная мебель с декором (картина, экран, телевизор на стене).
 *
 * Логика у них одна — положение на стене и границы, — различается только слой, поэтому
 * стор создаётся фабрикой на слой. Куда попадает новый объект, решает раздел каталога
 * (`wallFixturesStoreFor`), а кто владеет уже висящим — ищется по id (`wallFixtureOwner`).
 * Электрика живёт в своём сторе: у неё ещё светильники на потолке.
 */
import { create, type StoreApi, type UseBoundStore } from "zustand";
import {
	LayerKind,
	layerKindOfCatalogSection,
	uid,
	wallLength,
	type CatalogRef,
	type CatalogSectionKey,
	type Fixture,
} from "@/shared/plan";
import { useWallsStore } from "./wallsStore";

/** Держит объект в границах стены: вдоль — по длине, по высоте — до потолка. */
const clampFixture = (fixture: Fixture, length: number, height: number): Fixture => ({
	...fixture,
	offset: Math.max(0, Math.min(length, fixture.offset)),
	elevation: Math.max(0, Math.min(height, fixture.elevation)),
});

export interface WallFixturesLayerState {
	items: Record<string, Fixture>;
	visible: boolean;

	addFixture: (
		wallId: string,
		ref: CatalogRef,
		offset: number,
		elevation: number,
		side: 1 | -1,
	) => string | null;
	updateFixture: (id: string, patch: Partial<Omit<Fixture, "id" | "wallId">>) => void;
	setFixturePlacement: (id: string, offset: number, elevation: number) => void;
	setFixtureWall: (id: string, wallId: string, offset: number, side: 1 | -1) => void;

	setVisible: (visible: boolean) => void;
	toggleVisible: () => void;

	restoreFixtures: (fixtures: Record<string, Fixture>) => void;
}

export type WallFixturesStore = UseBoundStore<StoreApi<WallFixturesLayerState>>;

/** Стор объектов на стенах одного слоя; `idPrefix` — чтобы id разных слоёв не путались. */
const createWallFixturesStore = (idPrefix: string): WallFixturesStore =>
	create<WallFixturesLayerState>((set) => ({
		items: {},
		visible: true,

		addFixture: (wallId, ref, offset, elevation, side) => {
			const wallsState = useWallsStore.getState();
			const wall = wallsState.items.walls[wallId];
			if (!wall) return null;
			const length = wallLength(wallsState.items, wall);
			if (length < 100) return null;

			const id = uid(idPrefix);
			const fixture = clampFixture(
				{ id, wallId, ref, offset, elevation, side },
				length,
				wall.height,
			);
			set((s) => ({
				items: { ...s.items, [id]: fixture },
				visible: true,
			}));
			return id;
		},

		updateFixture: (id, patch) =>
			set((s) => {
				const fixture = s.items[id];
				if (!fixture) return s;

				const wallsState = useWallsStore.getState();
				const wall = wallsState.items.walls[fixture.wallId];
				if (!wall) return s;

				const next = clampFixture(
					{ ...fixture, ...patch },
					wallLength(wallsState.items, wall),
					wall.height,
				);
				return { items: { ...s.items, [id]: next } };
			}),

		setFixturePlacement: (id, offset, elevation) =>
			set((s) => {
				const fixture = s.items[id];
				if (!fixture) return s;

				const wallsState = useWallsStore.getState();
				const wall = wallsState.items.walls[fixture.wallId];
				if (!wall) return s;

				const next = clampFixture(
					{ ...fixture, offset, elevation },
					wallLength(wallsState.items, wall),
					wall.height,
				);
				return { items: { ...s.items, [id]: next } };
			}),

		setFixtureWall: (id, wallId, offset, side) =>
			set((s) => {
				const fixture = s.items[id];
				if (!fixture) return s;

				const wallsState = useWallsStore.getState();
				const wall = wallsState.items.walls[wallId];
				if (!wall) return s;

				const next = clampFixture(
					{ ...fixture, wallId, offset, side },
					wallLength(wallsState.items, wall),
					wall.height,
				);
				return { items: { ...s.items, [id]: next } };
			}),

		setVisible: (visible) => set({ visible }),
		toggleVisible: () => set((s) => ({ visible: !s.visible })),

		restoreFixtures: (fixtures) => set({ items: fixtures }),
	}));

/** Сантехника на стенах: умывальник, полотенцесушитель, стояки, коллектор, водорозетки */
export const usePlumbingStore = createWallFixturesStore("f");
/** Мебель и декор на стенах: картины, экран, телевизор на стене */
export const useFurnitureFixturesStore = createWallFixturesStore("fw");

/** Стор, куда попадает объект раздела на стене; электрика — не здесь, у неё свой стор. */
export const wallFixturesStoreFor = (section: CatalogSectionKey): WallFixturesStore | null =>
	layerKindOfCatalogSection(section) === LayerKind.Plumbing
		? usePlumbingStore
		: layerKindOfCatalogSection(section) === LayerKind.Furniture
			? useFurnitureFixturesStore
			: null;

/** Стор, в котором висит объект с этим id; null — такого нет (или это электрика). */
export const wallFixtureOwner = (id: string): WallFixturesStore | null => {
	if (id in usePlumbingStore.getState().items) return usePlumbingStore;
	if (id in useFurnitureFixturesStore.getState().items) return useFurnitureFixturesStore;
	return null;
};
